// src/com/dc_temp_info/DcTempInfo.js
import React, { useEffect, useRef, useState } from 'react';
//import { styled } from '@mui/material/styles';
import { Button, IconButton, CircularProgress } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune'; // 설정화면.
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import {ContentConainer, ContentHeader, ControlBox, ToolBox} from '../../common/TopViewControl';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import { authTableTreeRepo, dcTempInfoListRepo, userInfoRepo } from '../../value/AppModel';
import { LIST_DC_TEMP_INFO, EDIT_DC_TEMP_INFO } from './DcTempInfoGql';
import { DcTempInfoDictionary } from './DcTempInfoDict';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import FlexyTable from '../../common/flexytable/FlexyTable';
import DcTempInfoInput from './DcTempInfoInput';

/**
 * (DC_TEMP_INFO) 정보 View Controller
 * DC 별 기준온도 설정
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-12-06 13:02:00 using DbdbTap
 * https://www.dbdbtap.com
 */
export default function DcTempInfo({
    viewServiceTags // for switching view
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const [editorState, setEditorState] = useState(AppObject.EmptyViewerState);
    const dcTempInfoListData = useReactiveVar(dcTempInfoListRepo);
    const checkAuth = useAuthorizationCheck();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);

    // ##### Call GraphQL to get List #####
    const [getDcTempInfoList, responseList] = useLazyQuery(LIST_DC_TEMP_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: DcTempInfoDictionary.errorTitle.Get, message: [error.message]})
    });
    
    // ##### GraphQL Mutation. 'onError' for prevent from printing error at console (Uncaught exception) ###
    const [editDcTempInfo, responseEdit] = useMutation( EDIT_DC_TEMP_INFO, {
		onCompleted: (data, option) => onCompleteEdit(data, option),
		onError: (error) => addAlert({title: DcTempInfoDictionary.errorTitle.Edit, message: [error.message]})
	} );

    const getDcTempInfoListNow = () => {
        getDcTempInfoList();
        lastReloadRef.current = new Date().getTime()/1000;
    };

    useEffect(() => {
        const diff = new Date().getTime()/1000 - dcTempInfoListData.when;
        if(diff > 300)
            getDcTempInfoListNow();
    }, []);


    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.dcTempInfoList) {
            dcTempInfoListRepo({when:new Date().getTime()/1000, records:data.dcTempInfoList});
        }
    };

    const onCompleteEdit = (data, clientOption) => {
        if(data.dcTempInfoEdit.ok) {
            setEditorState(AppObject.EmptyViewerState);
            getDcTempInfoListNow();
            enqueueSnackbar("온도관제 설정이 저장되었습니다.", {variant:'success', autoHideDuration:2000});
        }
        else addAlert({title:DcTempInfoDictionary.errorTitle.Edit, message:[data.dcTempInfoEdit.message]});
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 60) {
            // Too short to reload
            enqueueSnackbar(AppWord.ReloadNotInOneMinute, {variant:'info', autoHideDuration:2000});
        }
        else getDcTempInfoListNow();
    };

    const onRequestSave = (item, isEdit) => {
        const mutateParam = {variables:{dcTempInfo:item}};
        editDcTempInfo(mutateParam);
    };

    const onClickEdit = () => {
        const config = dcTempInfoListData.records.find((item)=>item.dcCd===sessionInfo.user.dscIdAs);
        if(config) setEditorState({open:true, item:config});
        else addAlert({
            title: '사용자 소속의 DC 정보를 찾을 수 없습니다.',
            message: [
                '관리자에게 문의하세요.',
                `사용자 소속 DC 코드: ${sessionInfo.user.dscIdAs}`,
            ]
        })
    };

    const canEdit = checkAuth(authTableTree.tree.pr_dc_temp_info_edit);

    const loadingAny = responseList.loading || responseEdit.loading;

    return(
        <ContentConainer>
            {viewServiceTags}
            <ContentHeader>
                <ControlBox>
                    &nbsp;
                </ControlBox>
                <ToolBox>
                    {
                        loadingAny ?
                        <CircularProgress size={20} />
                        :
                        <IconButton
                            color={AppStyle.mui.inherit}
                            onClick={onClickReload}
                        >
                            <CachedIcon />
                        </IconButton>
                    }
                    {
                        canEdit &&
                        <Button
                            color={AppStyle.mui.primary}
                            style={{marginLeft:10}}
                            onClick={onClickEdit}
                            disabled={
                                loadingAny
                            }
                            variant={AppStyle.mui.contained}
                            startIcon={<TuneIcon />}
                        >
                            온도제한 설정
                        </Button>
                    }
                </ToolBox>
            </ContentHeader>
            <FlexyTable
                name={DcTempInfoDictionary.tableProps.tableId}
                tableProps={DcTempInfoDictionary.tableProps}
                records={dcTempInfoListData.records}
                tools={[]}
            />
            <DcTempInfoInput
                open={editorState.open}
                item={editorState.item}
                onClose={()=>setEditorState(AppObject.EmptyViewerState)}
                responseSaving={responseEdit}
                onRequestSave={onRequestSave}
            />
        </ContentConainer>
    );
}
